import type { Account, Blackout, BulkChanges, BulkField, BulkResult } from "./api";
import { STATUS_TEXT } from "./accounts";
import { weekGroupLabel } from "./plan";
import { blackoutProblem, blackoutText, seasonText } from "./seasons";

export const BULK_FIELDS: readonly BulkField[] = ["status", "week_group", "window", "season", "blackouts"];

export const BULK_FIELD_TEXT: Record<BulkField, string> = {
  status: "Status",
  week_group: "Visit weeks",
  window: "Service window",
  season: "Season",
  blackouts: "Blackout windows",
};

export interface BulkForm {
  pick: Record<BulkField, boolean>;
  status: Account["status"];
  weekGroup: string;
  windowStart: string;
  windowEnd: string;
  seasonStart: string;
  seasonEnd: string;
  blackouts: Blackout[];
}

export const EMPTY_BULK: BulkForm = {
  pick: { status: false, week_group: false, window: false, season: false, blackouts: false },
  status: "REG",
  weekGroup: "1",
  windowStart: "",
  windowEnd: "",
  seasonStart: "",
  seasonEnd: "",
  blackouts: [],
};

export function toggled(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Add or remove all the shown accounts; a selection outside the shown rows stays. */
export function withAll(selected: ReadonlySet<string>, ids: readonly string[], on: boolean): Set<string> {
  const next = new Set(selected);
  for (const id of ids) {
    if (on) next.add(id);
    else next.delete(id);
  }
  return next;
}

export const allPicked = (selected: ReadonlySet<string>, ids: readonly string[]) => ids.length > 0 && ids.every((id) => selected.has(id));

/** Rule T-6: only an empty selection turns a bulk action off, never its size. */
export const editOff = (picked: number) => picked === 0;

export const pinsOff = (pins: number) => pins === 0;

export const editText = (count: number) => (count === 1 ? "Edit 1 account" : `Edit ${count} accounts`);

export const changedText = (count: number) => `Changed ${count === 1 ? "1 account" : `${count} accounts`}. You can undo it for 24 hours.`;

/** The picked accounts whose front door is a geocode that nobody has checked. */
export function pinIds(accounts: readonly Account[], ids: readonly string[]): string[] {
  const geocoded = new Set(accounts.filter((account) => account.door?.source === "geocode").map((account) => account.id));
  return ids.filter((id) => geocoded.has(id));
}

const pinText = (count: number) => (count === 1 ? "1 pin" : `${count} pins`);

export const confirmPinsText = (count: number) => `Confirm ${pinText(count)}? Each pin stays where it is now.`;

export const pinsDoneText = (confirmed: number, placed: number) =>
  `${pinText(confirmed)} confirmed, ${placed} already placed. You can undo it for 24 hours.`;

const sheetFieldsText = (fields: readonly BulkField[]) => {
  const names = fields.map((field) => `the ${BULK_FIELD_TEXT[field].toLowerCase()}`);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
};

/** Rule T-5: the bulk edit saves, but the next import writes the sheet value of these fields again. */
export function sheetText(sheet: BulkResult["sheet"], total: number): string | null {
  if (!sheet || sheet.accounts === 0 || sheet.fields.length === 0) return null;
  const who =
    total === 1
      ? "This account comes from the sheet."
      : `${sheet.accounts} of these ${total} accounts ${sheet.accounts === 1 ? "comes" : "come"} from the sheet.`;
  return `${who} The next import writes the sheet value of ${sheetFieldsText(sheet.fields)} again.`;
}

/** The body of the picked fields only, or the first problem of the form. */
export function bulkChanges(form: BulkForm): BulkChanges | string {
  const changes: BulkChanges = {};
  if (form.pick.status) changes.status = form.status;
  if (form.pick.week_group) changes.week_group = form.weekGroup;
  if (form.pick.window) {
    if (Boolean(form.windowStart) !== Boolean(form.windowEnd)) return "Give the start and the end of the service window, or neither.";
    if (form.windowStart && form.windowStart >= form.windowEnd) return "The service window must end after it starts.";
    changes.window_start = form.windowStart || null;
    changes.window_end = form.windowEnd || null;
  }
  if (form.pick.season) {
    const allYear = !form.seasonStart || !form.seasonEnd;
    changes.season_start = allYear ? null : form.seasonStart;
    changes.season_end = allYear ? null : form.seasonEnd;
  }
  if (form.pick.blackouts) {
    const problem = blackoutProblem(form.blackouts);
    if (problem) return problem;
    changes.blackouts = form.blackouts;
  }
  return Object.keys(changes).length ? changes : "Choose a field to change.";
}

function isPair(value: unknown): value is [string | null, string | null] {
  return Array.isArray(value) && value.length === 2;
}

/** A value of the preview as text, the same words as the account page. */
export function valueText(field: BulkField, value: unknown): string {
  if (field === "status") return STATUS_TEXT[value as Account["status"]] ?? String(value);
  if (field === "week_group") return typeof value === "string" ? weekGroupLabel(value) : "–";
  if (field === "window") return isPair(value) && value[0] && value[1] ? `${value[0]}–${value[1]}` : "Any time";
  if (field === "season") return isPair(value) && value[0] && value[1] ? seasonText([value[0], value[1]]) : "All year";
  return Array.isArray(value) ? blackoutText(value as Blackout[]) : "None";
}
